import type { BossPhase, DockId, EnemyRole, ShipId } from '../data';
import { Quat, Vec3 } from './core/math';
import type { ShieldSpec } from './loadout';

export type Team = 'player' | 'enemy';
/** `convoy` : vaisseau allié escorté (phase D), dans l'équipe du joueur. */
export type ShipKind = 'player' | EnemyRole | 'boss' | 'convoy';
/** Origine d'un dégât, conservée même si le tireur a disparu (implementation_ue §6). */
export type DamageSource = 'player' | EnemyRole | 'boss' | 'unknown';
export type WeaponKind = 'laser' | 'missile' | 'swarm' | 'bolt' | 'railgun' | 'volley' | 'special' | 'detonation';

export type Envelope = 'cruise' | 'boost' | 'reverse' | 'brake';

/** Paramètres du moteur de vol commun au joueur et à l'IA. */
export interface MotorProfile {
  cruiseMaxSpeed: number;
  boostMaxSpeed: number;
  reverseMaxSpeed: number;
  cruiseAcceleration: number;
  boostAcceleration: number;
  reverseAcceleration: number;
  lateralThrustScale: number;
  verticalThrustScale: number;
  /** Taux angulaires maximaux par axe local (rad/s) : x = tangage, y = lacet, z = roulis. */
  maxRates: Vec3;
  /** Plafond de la vitesse angulaire totale (rad/s), ou Infinity. */
  maxTotalRate: number;
  releaseDeceleration: number;
  brakeDeceleration: number;
  brakeThrustFactor: number;
  forwardOvershootDeceleration: number;
  reverseOvershootDeceleration: number;
  reverseSpeedThreshold: number;
  boostInputThreshold: number;
  reverseInputThreshold: number;
  turningBoost: number;
  /** Accélération angulaire (rad/s²) : Infinity = rotation immédiate (IA, souris directe). */
  angularAcceleration: number;
  /** Jauge de boost (secondes) : Infinity pour l'IA. */
  boostCapacity: number;
  boostDrain: number;
  boostRegen: number;
  boostRegenDelay: number;
  boostMinToStart: number;
}

/** Commande de pilotage identique pour le joueur et l'IA (plan §3). */
export interface ShipCommand {
  /** Poussée locale −1..1 : x = droite, y = haut, z = avant. */
  thrust: Vec3;
  /** Vitesse angulaire locale demandée (rad/s), autour des axes X, Y, Z locaux. */
  turnRate: Vec3;
  /**
   * Rotation souris directe (radians pour ce tick, axes locaux) : immédiate, non plafonnée, hors inertie
   * (comportement Unreal). Les autres commandes passent par `turnRate`.
   */
  turnImpulse: Vec3;
  boost: boolean;
  brake: boolean;
}

export const emptyCommand = (): ShipCommand => ({
  thrust: new Vec3(),
  turnRate: new Vec3(),
  turnImpulse: new Vec3(),
  boost: false,
  brake: false,
});

export interface FlightState {
  envelope: Envelope;
  boosting: boolean;
  braking: boolean;
  reversing: boolean;
  forwardInput: number;
  signedForwardSpeed: number;
  /** Secondes écoulées depuis le début du boost courant (−1 hors boost). */
  boostElapsed: number;
}

export class Ship {
  readonly position: Vec3;
  readonly velocity = new Vec3();
  rotation: Quat;
  health: number;
  alive = true;
  readonly flight: FlightState = {
    envelope: 'cruise',
    boosting: false,
    braking: false,
    reversing: false,
    forwardInput: 0,
    signedForwardSpeed: 0,
    boostElapsed: -1,
  };
  /** Dernière commande appliquée (lecture pour le rendu et la télémétrie). */
  command: ShipCommand = emptyCommand();
  /** Normale de l'obstacle touché pendant le dernier déplacement. */
  lastBlockNormal: Vec3 | null = null;
  /** Temps de simulation du dernier dégât reçu. */
  lastDamageTime = -Infinity;
  /** Vitesse angulaire locale effective (rad/s), lue par le rendu pour l'inclinaison des coques. */
  readonly angularVelocity = new Vec3();
  /** État de l'inertie de rotation : vitesse angulaire issue de `turnRate` seulement. */
  readonly turnState = new Vec3();
  /** Drone en piqué ou en annonce de piqué (limite de piqués simultanés, HUD). */
  diving = false;
  /** Aucun dégât avant cet instant (appontage, réapparition). */
  invulnerableUntil = -Infinity;
  boostEnergy: number;
  boostRegenTimer = 0;
  /** Jauge vidée : le boost reste coupé jusqu'au seuil de redémarrage. */
  boostExhausted = false;
  /** Bouclier (phase C) : absorbe les dégâts avant la coque, se régénère après un délai sans dégât. */
  shieldSpec: ShieldSpec | null = null;
  shield = 0;
  /** Délai de régénération en cours : normal, ou plus long après un bouclier rompu. */
  shieldDelay = 0;

  constructor(
    readonly id: number,
    readonly kind: ShipKind,
    readonly team: Team,
    position: Vec3,
    rotation: Quat,
    /** Modifiable : changement d'équipement à quai. */
    public maxHealth: number,
    readonly radius: number,
    public motor: MotorProfile,
  ) {
    this.position = position.clone();
    this.rotation = rotation.clone();
    this.health = maxHealth;
    this.boostEnergy = motor.boostCapacity;
  }

  forward(): Vec3 { return this.rotation.forward(); }
  right(): Vec3 { return this.rotation.right(); }
  up(): Vec3 { return this.rotation.up(); }

  /** Point local (repère du vaisseau) exprimé dans le monde. */
  localToWorld(local: readonly number[] | Vec3): Vec3 {
    const v = local instanceof Vec3 ? local : Vec3.from(local);
    return this.position.add(this.rotation.rotate(v));
  }

  healthRatio(): number { return this.maxHealth > 0 ? Math.max(0, this.health) / this.maxHealth : 0; }

  get maxShield(): number { return this.shieldSpec?.capacity ?? 0; }

  /** Monte un bouclier plein (début de session, réapparition). */
  equipShield(spec: ShieldSpec | null): void {
    this.shieldSpec = spec;
    this.shield = spec?.capacity ?? 0;
    this.shieldDelay = spec?.regenDelay ?? 0;
  }

  /** Régénération après le délai sans dégât (`now` = temps de simulation). */
  regenShield(now: number, dt: number): void {
    const spec = this.shieldSpec;
    if (!spec || !this.alive || this.shield >= spec.capacity) return;
    if (now - this.lastDamageTime < this.shieldDelay) return;
    this.shield = Math.min(spec.capacity, this.shield + spec.regenRate * dt);
  }

  damageSource(): DamageSource {
    return this.kind === 'player' || this.kind === 'convoy' ? 'player' : this.kind;
  }
}

export type ProjectileKind = 'missile' | 'swarm' | 'bolt' | 'railgun' | 'volley' | 'special' | 'bossBolt';

export interface Projectile {
  id: number;
  kind: ProjectileKind;
  team: Team;
  ownerId: number;
  source: DamageSource;
  position: Vec3;
  velocity: Vec3;
  radius: number;
  damage: number;
  /** Rayon de dégâts de zone (0 pour un tir ponctuel). */
  blastRadius: number;
  life: number;
  age: number;
  alive: boolean;
  color: [number, number, number];
  targetId: number | null;
  /** Guidage de la nuée. */
  homingActivation: number;
  homingActive: boolean;
  targetLost: boolean;
  offTargetSeconds: number;
  /** Vitesse minimale et maximale du guidage. */
  minSpeed: number;
  maxSpeed: number;
  homingAcceleration: number;
}

export interface Pickup {
  id: number;
  kind: 'combat' | 'arena';
  position: Vec3;
  life: number;
  amount: number;
  alive: boolean;
  attracted: boolean;
}

export type LockState = 'none' | 'detected' | 'acquiring' | 'locked';

export interface PlayerWeaponsState {
  shipId: ShipId;
  missiles: number;
  maxMissiles: number;
  swarm: number;
  maxSwarm: number;
  laserCooldown: number;
  lastMissileTime: number;
  lastSwarmTime: number;
  lockState: LockState;
  lockTargetId: number | null;
  lockElapsed: number;
  /** Dernière cible retenue par la recherche (hystérésis « précédente »). */
  previousCandidateId: number | null;
  primaryWasDown: boolean;
  secondaryWasDown: boolean;
  /** Chaleur du laser 0..1 ; surchauffe = tir coupé jusqu'au refroidissement. */
  heat: number;
  overheated: boolean;
  overheatTimer: number;
  heatCoolTimer: number;
}

/** Contrôle du joueur : vol libre ou séquences du porteur (phase B). */
export type ControlMode = 'flight' | 'docking' | 'docked' | 'launching' | 'destroyed';

export interface PlayerControl {
  mode: ControlMode;
  /** Temps écoulé dans le mode courant. */
  elapsed: number;
  /** Pose de départ de la séquence scriptée. */
  fromPosition: Vec3;
  fromRotation: Quat;
  /** Lancement automatique (réapparition, début de mission) après ce délai à quai ; Infinity sinon. */
  autoLaunchAt: number;
  /** La poussée avant a été relâchée depuis l'appontage (évite un relancement involontaire). */
  forwardReleased: boolean;
  /** Décollage demandé depuis le menu du dock. */
  launchRequested: boolean;
  /** Dock où l'on est amarré, ou le dernier quitté : lieu de réapparition. */
  dockId: DockId;
  missileTimer: number;
  swarmCarry: number;
  reserves: number;
  deaths: number;
  docks: number;
  serviceSeconds: number;
  /** Charges restantes de réparation d'urgence (rechargées au porteur). */
  repairCharges: number;
  /** Module de saut : charge écoulée (s), −1 au repos ; zone visée ; touche tenue au tick précédent. */
  jumpCharge: number;
  jumpTarget: string | null;
  jumpWasDown: boolean;
  /** Destination choisie (touche G) ; null = première zone possible. */
  jumpSelect: string | null;
  jumpCycleWasDown: boolean;
}

export interface BossStatus {
  phase: BossPhase;
  cue: string;
  volleyCharging: boolean;
  specialTelegraph: boolean;
  recovery: number;
}
